<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" data-cy="podMenu-tools-imprintSubtitles-activator" />
    </template>

    <v-form ref="form" v-on:submit.prevent="formSubmit"
            data-cy="podMenu-tools-imprintSubtitles-form">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('routes.pod.menuImprintSubtitles.imprintSubtitles') }}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="scrollable mt-4">
          <div class="bg-primary-darker mb-4" data-cy="podMenu-tools-imprintSubtitles-selectFile">
            {{ assetName }}
          </div>

          <div data-cy="podMenu-tools-imprintSubtitles-selectFontFamily">
            <v-select
              v-model="fontFamily"
              :rules="fontFamilyRules"
              :items="fontFamilies"
              :label="$t('routes.pod.menuImprintSubtitles.selectFontFamily')"
              variant="solo-filled"
              :list-props="{ tabindex: '0' }"
              flat persistent-placeholder
            />
          </div>

          <div data-cy="podMenu-tools-imprintSubtitles-selectFont">
            <v-select
              v-model="font"
              :rules="fontRules"
              :items="fonts"
              :label="$t('routes.pod.menuImprintSubtitles.selectFont')"
              :disabled="!fontFamily"
              variant="solo-filled"
              :list-props="{ tabindex: '0' }"
              flat persistent-placeholder
            />
          </div>

          <div>
            <ColorPicker
              v-model="fontColor"
              :label="$t('routes.pod.menuImprintSubtitles.selectFontColor')"
              :rules="fontColorRules"
              mode="rgba"
            />
          </div>

          <div>
            <ColorPicker
              v-model="backgroundColor"
              :label="$t('routes.pod.menuImprintSubtitles.selectBackgroundColor')"
              :rules="backgroundColorRules"
              mode="rgba"
            />
          </div>

          <div v-show="successMessage" class="bg-success"
               data-cy="podMenu-tools-imprintSubtitles-successMessage"
          >
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="bg-error"
               data-cy="podMenu-tools-imprintSubtitles-errorMessage"
          >
            {{ errorMessage }}
          </div>

          <UtilityPodsErrors/>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            :disabled="isRunJobsDisabled"
            data-cy="podMenu-tools-imprintSubtitles-imprintVideo-btn"
          >
            {{ $t('routes.pod.menuImprintSubtitles.imprintVideo') }}
          </v-btn>

          <v-btn variant="text" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import UtilityPodsErrors from '@/components/globals/UtilityPodsErrors.vue';
import ColorPicker from '@/components/globals/ColorPicker.vue';

export default {
  components: { ColorPicker, UtilityPodsErrors },
  mixins: [DialogActionKeys, DialogModel],
  name: "ImprintSubtitleDialog",
  props: {
    asset: {
      type: Object,
      required: false,
    }
  },
  data() {
    return {
      successMessage: "",
      errorMessage: "",

      fontFamily: null,
      fontFamilyRules: [
        v => !!v || this.$t('routes.pod.menuImprintSubtitles.fontFamilyRequired'),
      ],

      font: '',
      fontRules: [
        v => !!v || this.$t('routes.pod.menuImprintSubtitles.fontRequired'),
      ],

      fontColorMenu: false,

      isFontColorPickerVisible: false,
      fontColor: '#FFFFFFFF',
      fontColorRules: [
        v => !!v || this.$t('routes.pod.menuImprintSubtitles.fontColorRequired'),
      ],

      backgroundColorMenu: false,
      isFontBackgroundColorPickerVisible: false,

      backgroundColor: '#00000000',
      backgroundColorRules: [
        v => !!v || this.$t('routes.pod.menuImprintSubtitles.backgroundColorRequired'),
      ],
      preventDoubleClick: false,
      shouldActivateImprintButton: true,

    };
  },
  mounted: function() {
    this.resetForm();
  },
  watch: {
    dialog(visible) {
      if (!visible) {
        this.resetForm();
      } else {
        this.checkIsRunnable();
      }
    },
    isReadyForRunnableCheck: function(newValue) {
      if (newValue) {
        this.checkIsRunnable();
      }
    },
    fontFamily() {
      this.font = null;
    },
  },
  methods: {
    resetForm() {
      this.successMessage = "";
      this.errorMessage = "";
      this.fontFamily = this.defaultFontFamily;
      this.fontColorMenu = false;
      this.fontColor = '#FFFFFFFF';
      this.backgroundColorMenu = false;
      this.backgroundColor = '#00000000';
      this.preventDoubleclick = false;
      this.shouldActivateImprintButton = true;
      this.isFontColorPickerVisible = false;
      this.isFontBackgroundColorPickerVisible = false;

      this.$nextTick(() => {
        this.font = this.defaultFont;
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },
    checkIsRunnable: async function() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.getImprintSubtitlesService) {
        this.errorMessage = "Imprint subtitles service not found!";
        return;
      }

      let utilityPayload = {
        serviceAlias: this.getImprintSubtitlesService.alias,
        pods: [this.pod],
        assetID: this.asset.id,
      }

      const serviceStore = useServiceStore();
      try {
        await serviceStore.updatePodsUtility(utilityPayload);
        const runnable = serviceStore.getRunnablePods;
        if (runnable.length !== 0) {
          this.shouldActivateImprintButton = true;
        } else {
          this.shouldActivateImprintButton = false;
          // throw { response: { data: { error: { message: this.$t("routes.pod.menuImprintSubtitles.operationError")}}}}
        }
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }

        this.successMessage = '';
      }
    },
    formSubmit: async function() {
      this.errorMessage = "";
      this.successMessage = "";

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.preventDoubleclick = true;
      setTimeout(() => {
        this.preventDoubleclick = false;
      }, 10000);

      const serviceStore = useServiceStore();
      const runnable = serviceStore.getRunnablePods;

      if (runnable.length === 0) {
        throw { response: { data: { error: { message: this.$t("routes.pod.menuImprintSubtitles.operationError")}}}};
      }

      // reverse the RRGGBBAA to AABBGGRR and invert the alpha
      let fontColor = this.fontColor.replace('#', '').match(/../gm);
      let alphaFC = (0xff - parseInt('0x' + fontColor[3])).toString(16).padStart(2, '0');
      fontColor[3] = alphaFC;
      fontColor = fontColor.reverse().join("");

      // reverse the RRGGBBAA to AABBGGRR and invert the alpha
      let backgroundColor = this.backgroundColor.replace('#', '').match(/../gm);
      let alphaBC = (0xff - parseInt('0x' + backgroundColor[3])).toString(16).padStart(2, '0');
      backgroundColor[3] = alphaBC;
      backgroundColor = backgroundColor.reverse().join("");

      let payload = {
        serviceAlias: this.getImprintSubtitlesService.alias,
        utilityPods: runnable,
        params: {
          assetid: this.asset.id,
          font: this.fontFamily + " " + this.font,
          fontColor: fontColor,
          backgroundColor: backgroundColor
        }
      };

      try {
        await serviceStore.runService(payload);
        if (this.utilityPodsErrors.length === 0) {
          this.successMessage = this.$t("routes.pod.menuImprintSubtitles.operationQueued");
        }

        this.shouldActivateImprintButton = false;
        setTimeout(() => {
          this.shouldActivateImprintButton = true;
        }, 10000);
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }

        this.preventDoubleclick = false;
        this.successMessage = '';
      }
    },
    handleFontColorPickerInputFocus: function(isFocused) {
      if (isFocused) {
        this.isFontBackgroundColorPickerVisible = false;
        this.isFontColorPickerVisible = isFocused;
      }
    },
    handleFontBackgroundColorPickerFocus: function(isFocused) {
      if (isFocused) {
        this.isFontColorPickerVisible = false;
        this.isFontBackgroundColorPickerVisible = isFocused;
      }
    }
  },
  computed: {
    assetName: function () {
      return this.asset?.name ?? '';
    },
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getImprintSubtitlesService: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getImprintSubtitles;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    defaultFontFamily: function() {
      return this.fontFamilies[0];
    },
    defaultFont: function() {
      return this.fonts[0];
    },
    fontFamilies: function() {
      return [
        'Liberation Mono',
        'Liberation Sans',
        'Liberation Sans Narrow',
        'Liberation Serif',
        'Montserrat',
        'Roboto',
      ];
    },
    fonts: function() {
      let fonts = {
        'Liberation Mono': ['Regular', 'Italic', 'Bold', 'Bold Italic'],
        'Liberation Sans': ['Regular', 'Italic', 'Bold', 'Bold Italic'],
        'Liberation Sans Narrow': ['Regular', 'Italic', 'Bold', 'Bold Italic'],
        'Liberation Serif': ['Regular', 'Italic', 'Bold', 'Bold Italic'],
        'Montserrat': ['Black', 'Black Italic', 'Bold', 'Bold Italic', 'ExtraBold',
                       'ExtraBold Italic', 'ExtraLight', 'ExtraLight Italic', 'Italic',
                       'Light', 'Light Italic', 'Medium', 'Medium Italic', 'Regular',
                       'SemiBold', 'SemiBold Italic', 'Thin', 'Thin Italic'],
        'Roboto': ['Black', 'Black Italic', 'Bold', 'Bold Italic', 'Italic', 'Light',
                    'Light Italic', 'Medium', 'Medium Italic', 'Regular', 'Thin', 'Thin Italic'],
      };
      return fonts[this.fontFamily];
    },
    isRunJobsDisabled: function() {
      if (!this.isReadyForRunnableCheck ||
          !this.shouldActivateImprintButton ||
          this.preventDoubleClick == true
      ) {
        return true;
      }

      return false;
    },
    isReadyForRunnableCheck: function() {
      return this.backgroundColor && this.asset && this.fontFamily && this.font && this.fontColor;
    },
  },

}
</script>

<style>
</style>
